import React, { memo, useCallback, useMemo, useState } from 'react';
import { InputContainer, Search } from 'finform';
import { Api } from 'laravel-request';
import { useQueryClient } from '@tanstack/react-query';
import { ActiveConstants, ActiveTagConstants } from 'finhelper';
import { useAppDispatch, useAppSelector } from '#app/hooks/useAppStore';
import { getActiveSubcategories } from '#app/actions/interface';
import { useActives } from '#app/pages/IncomeAndExpenses/components/ActivesContext';
import { useCreateSubcategoryOnTheFly } from '#app/hooks/useCreateSubcategoryOnTheFly';
import {
  extractActiveIdsFromUpdateResponse,
  patchOperationsListCache,
} from '#app/helpers/operations/patchOperationsListCache';
import { filterSubcategoriesByCategories } from '#app/pages/IncomeAndExpenses/components/hooks/subcategoryCategoryUtils';

const SearchSubCategory = memo(({ item, subcategoryType, subcategories, finishEdit }) => {
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const client = useAppSelector((s) => s.interfaceComponents.client);
  const currencyId = useAppSelector((s) => s.interfaceComponents.currency?.id);

  const listKind = useMemo(
    () => (subcategoryType === ActiveTagConstants.INCOME ? 'incomes' : 'spendings'),
    [subcategoryType]
  );

  const getActiveSubcategoriesFn = useCallback(
    () => dispatch(getActiveSubcategories()),
    [dispatch]
  );
  const [search, setSearch] = useState(item.subcategory_text);
  const [selectedItem, setSelectedItem] = useState(null);
  const { setPreparedActives } = useActives();

  const customTypeConstant =
    subcategoryType === ActiveTagConstants.INCOME
      ? ActiveConstants.CUSTOM_INCOME
      : ActiveConstants.CUSTOM_SPEND;

  const selectedCategory = item?.type_id
    ? [
        {
          id: item.type_id,
          type_id: item.custom_type_id ?? item.type_id,
        },
      ]
    : [];

  const filteredSubcategories = filterSubcategoriesByCategories(
    subcategories,
    selectedCategory,
    customTypeConstant
  );

  // Хук для создания подкатегории "на лету"
  const { createSubcategory } = useCreateSubcategoryOnTheFly({
    userId: client?.id || 0,
    typeId: subcategoryType,
    baseTypeId: item.type_id && !item.custom_type_id ? item.type_id : undefined,
    customTypeId: item.custom_type_id,
  });

  const patchSubcategoryInCache = useCallback(
    (response, activeId, subcategoryId, subcategoryText) => {
      if (!client?.id || !currencyId) return;
      const activeIds = extractActiveIdsFromUpdateResponse(response, activeId);
      patchOperationsListCache(queryClient, listKind, client.id, currencyId, activeIds, {
        subcategory_id: subcategoryId,
        subcategory_text: subcategoryText,
      });
    },
    [client?.id, currencyId, queryClient, listKind]
  );

  const storeActiveWithSubcategory = useCallback(
    (subcategoryId, subcategoryText) => {
      Api.putArg('active', 'subcategory', item.id, {
        subcategory_id: subcategoryId,
        user_id: client?.id || '',
      }).call((response) => {
        patchSubcategoryInCache(response, item.id, subcategoryId, subcategoryText);
        getActiveSubcategoriesFn();
      });
    },
    [item.id, client?.id, getActiveSubcategoriesFn, patchSubcategoryInCache]
  );

  const updateItemInList = useCallback(
    (itemId, updates) => {
      setPreparedActives((prev) =>
        prev.map((year) => ({
          ...year,
          months: year.months.map((month) => ({
            ...month,
            dates: month.dates.map((date) => ({
              ...date,
              items: date.items.map((row) => (row.id === itemId ? { ...row, ...updates } : row)),
            })),
          })),
        }))
      );

      storeActiveWithSubcategory(updates.subcategory_id, updates.subcategory_text);
      finishEdit();
    },
    [storeActiveWithSubcategory, finishEdit, setPreparedActives]
  );

  const createNewSubcategory = useCallback(
    async (name, itemId, searchText) => {
      const result = await createSubcategory(name);

      if (result.success && result.subCategoryId) {
        updateItemInList(itemId, {
          subcategory_id: result.subCategoryId,
          subcategory_text: searchText,
        });
      }
    },
    [createSubcategory, updateItemInList]
  );

  const handleItemSelect = useCallback(
    (selected) => {
      if (!selected) return;

      setSelectedItem(selected);
      setSearch(selected.name);

      updateItemInList(item.id, {
        subcategory_id: selected.id,
        subcategory_text: selected.name,
      });
    },
    [item.id, updateItemInList]
  );

  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    setSelectedItem({ name: value });
  }, []);

  const onBlur = useCallback(() => {
    if (selectedItem && !selectedItem.id && selectedItem.name) {
      createNewSubcategory(selectedItem.name, item.id, search);
    } else if (selectedItem) {
      updateItemInList(item.id, {
        subcategory_id: selectedItem.id,
        subcategory_text: selectedItem.name,
      });
    }
  }, [search, selectedItem, item.id, createNewSubcategory, updateItemInList]);

  return (
    <div style={{ alignItems: 'center', display: 'flex' }}>
      <InputContainer
        style={{ height: '24px', maxWidth: '110px', minWidth: '110px', margin: '0 10px' }}
      >
        <Search
          style={{ height: '24px', width: '85px', padding: '0 2px 2px 8px' }}
          id={`incomes-and-expenses-subcategory-${item.id}`}
          search={search}
          onSearch={handleSearchChange}
          options={filteredSubcategories}
          onChange={handleItemSelect}
          value={null}
          onBlur={onBlur}
          icon={false}
          clearOnClickOutside={false}
        />
      </InputContainer>
    </div>
  );
});

export default SearchSubCategory;
